import { Actions, createEffect, ofType } from '@ngrx/effects';

import { Injectable, Inject } from '@angular/core';
import { environmentLoadedSuccess } from '../environment/environment.actions';
import { map, filter } from 'rxjs/operators';
import { sendAnalyticsEventAction, sendToGtag } from './analytics.actions';
import { Store, select } from '@ngrx/store';
import { AppState } from 'src/app/app.state';
import { ClientConfigurationState } from '../client-configuration/client-configuration.state';
import { Router, NavigationEnd } from '@angular/router';
import { DOCUMENT } from 'src/app/feature/injection-token/document';
import { WINDOW } from 'src/app/feature/injection-token/window';
import { selectClientConfiguration } from '../client-configuration/client-configuration.selectors';

@Injectable()
export class AnalyticsEffects {
	/**
	 * The client configuration state
	 */
	private clientConfig: ClientConfigurationState;

	/**
	 * Loads the the analytics script and sets the config.
	 */
	setupAnalytics$ = createEffect(() =>
		this.actions$.pipe(
			ofType(environmentLoadedSuccess),
			map(({ payload }) => {
				const document = this.getDocument();
				const script = document.createElement('script');
				const id = payload.isProduction ? 'G-1LERHTFQSZ' : 'G-WCJWJ51L7R';
				script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
				script.async = true;
				document.head.appendChild(script);

				const gtmScript = document.createElement('script');
				const gtmId = payload.isProduction ? 'GTM-PZNMHVF' : 'GTM-KQDN29K';
				gtmScript.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${gtmId}');`;
				document.head.appendChild(gtmScript);

				return sendToGtag({
					params: [
						'config',
						id,
						{
							anonymize_ip: true,
							send_page_view: false,
							custom_map: {
								dimension1: 'd_tenantName',
								dimension2: 'd_merchantId',
								dimension3: 'd_language',
							},
						},
					],
				});
			})
		)
	);

	/**
	 * Handles sending events to GA.
	 */
	sendEvent$ = createEffect(() =>
		this.actions$.pipe(
			ofType(sendAnalyticsEventAction),
			map((storeAction) => {
				const { action, data } = storeAction;
				return sendToGtag({
					params: ['event', action, data],
				});
			})
		)
	);

	/**
	 * Calls the gtag with the specific parameters.
	 */
	gtag$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(sendToGtag),
				map((action) => {
					const { gtag } = this.window;
					gtag.apply(gtag, action.params);
				})
			),
		{ dispatch: false }
	);

	/**
	 * @param document The document object
	 * @param actions$ The actions stream.
	 */
	constructor(
		@Inject(WINDOW) private window: Window,
		@Inject(DOCUMENT) private document: Document,
		private actions$: Actions,
		private store: Store<AppState>,
		router: Router
	) {
		this.store.pipe(select(selectClientConfiguration)).subscribe((clientConfig) => {
			this.clientConfig = clientConfig;
		});

		// We need this check as this module is used in various lazy loaded children and they all get their own instance
		if ((window as any).___AHP_ANALYTICS_SET) {
			return;
		}
		(window as any).___AHP_ANALYTICS_SET = true;

		router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
			let page = event.urlAfterRedirects.split('?').shift();
			const parts = page.split('/');

			// 4 because we should always start with a `/` which will add an empty string at the begining.
			if (parts.length >= 4) {
				page = `/:tenantName/:merchantId/:language/${parts.slice(4).join('/')}`;
			}

			const { tenantName, merchantId, language } = this.clientConfig;

			this.window.gtag('event', 'page_view', {
				page_path: page,
				d_tenantName: tenantName,
				d_merchantId: merchantId,
				d_language: language,
			});
		});
	}

	/**
	 * Returns the document object.
	 */
	private getDocument(): Document {
		return this.document;
	}
}
